import { useState, useEffect, useRef } from 'react'
import moment, { Duration } from 'moment'
import pluralize from 'pluralize'

export enum ETimerVariant {
  Primary = 'primary',
  Secondary = 'secondary',
}

interface ITimerResult {
  timeLeft: string | null
  warning: boolean
  timerExpired: boolean
}

export interface IUseTimerProps {
  startTime?: string
  variant: ETimerVariant
  warningTime?: { value: number; type: 'seconds' | 'minutes' | 'hours' | 'days' }
}

const useTimer = ({ startTime, variant, warningTime }: IUseTimerProps): ITimerResult => {
  const [timerExpired, setTimerExpired] = useState<boolean>(false)
  const [timeLeft, setTimeLeft] = useState<string | null>(null)
  const [warning, setWarning] = useState<boolean>(false)
  const intervalIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (startTime) {
      setTimerExpired(false)
      setWarning(false)
    }
  }, [startTime])

  useEffect(() => {
    if (!startTime) {
      return
    }

    const initializeTimer = () => {
      const now = moment()
      const start = moment(startTime)
      const diff = start.diff(now)

      if (diff > 0) {
        const duration: Duration = moment.duration(diff)
        setTimeLeft(getFormattedTime(duration, variant))

        if (warningTime && !warning && isWarningTimeReached(duration, warningTime)) {
          setWarning(true)
        }
      } else {
        setTimerExpired(true)
        setTimeLeft('00:00:00')
      }
    }

    initializeTimer()

    intervalIdRef.current = window.setInterval(initializeTimer, 1000)

    return () => {
      if (intervalIdRef.current !== null) {
        clearInterval(intervalIdRef.current)
      }
    }
  }, [startTime, variant, warningTime, warning])

  return { timeLeft, warning, timerExpired }
}

function getFormattedTime(duration: Duration, variant: ETimerVariant): string {
  const hours: number = duration.hours()
  const minutes: number = duration.minutes()
  const seconds: number = duration.seconds()
  let days: number
  let dayString: string
  let hourString: string
  let minuteString: string

  switch (variant) {
    case ETimerVariant.Primary:
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`
    case ETimerVariant.Secondary:
      days = duration.days()
      dayString = days ? pluralize('day', days, true) : ''
      hourString = hours ? pluralize('hour', hours, true) : ''
      minuteString = minutes ? pluralize('minute', minutes, true) : ''
      return `${dayString && dayString} ${hourString && hourString} ${minuteString && minuteString}`
    default:
      return ''
  }
}

function isWarningTimeReached(
  duration: Duration,
  warningTime: { value: number; type: 'seconds' | 'minutes' | 'hours' | 'days' }
): boolean {
  const { value, type } = warningTime

  switch (type) {
    case 'seconds':
      return duration.asSeconds() <= value
    case 'minutes':
      return duration.asMinutes() <= value
    case 'hours':
      return duration.asHours() <= value
    case 'days':
      return duration.asDays() <= value
    default:
      return false
  }
}

export { useTimer }
